import { IProgram } from "../interfaces/models/IEvent";
import { getDayKeyInTimezone } from "./dates";
import { sortProgramsBySchedule } from "./groupProgramsByDay";
import { programIdentityKey } from "./updateEventPrograms";

const venueText = (value?: string): string => (value ?? "").trim();

const hasVenue = (
  program: Pick<IProgram, "venueName" | "location">,
): boolean =>
  venueText(program.venueName) !== "" || venueText(program.location) !== "";

const clearEmptyVenue = (program: IProgram): IProgram => {
  const next = { ...program };
  if (!venueText(next.venueName)) {
    delete next.venueName;
  }
  if (!venueText(next.location)) {
    delete next.location;
  }
  return next;
};

const mirrorsVenue = (
  program: Pick<IProgram, "venueName" | "location">,
  dayVenue: Pick<IProgram, "venueName" | "location">,
): boolean =>
  hasVenue(program) &&
  venueText(program.venueName) === venueText(dayVenue.venueName) &&
  venueText(program.location) === venueText(dayVenue.location);

/**
 * A session carries its own venue only when that venue is not the day's venue.
 * The day's venue is the one on the earliest session, shown once for the day.
 */
export const sessionHasDifferentVenue = (
  session: Pick<IProgram, "venueName" | "location">,
  dayVenue: Pick<IProgram, "venueName" | "location"> | undefined,
): boolean => {
  if (!dayVenue || !hasVenue(session)) {
    return false;
  }
  return !mirrorsVenue(session, dayVenue);
};

const programsOnDay = (programs: IProgram[], dayKey: string): IProgram[] =>
  sortProgramsBySchedule(
    programs.filter(
      (program) => getDayKeyInTimezone(program.startTime, "UTC") === dayKey,
    ),
  );

/**
 * Later sessions do not inherit the day's venue. Only sessions that already
 * repeated the previous day's venue follow a change to the session that opens the day.
 */
const applyDayVenue = (
  programs: IProgram[],
  editedProgram: IProgram,
  previousPrograms: IProgram[],
): IProgram[] => {
  const dayKey = getDayKeyInTimezone(editedProgram.startTime, "UTC");
  const nextOnDay = programsOnDay(programs, dayKey);
  const dayVenueSession = nextOnDay[0];
  if (!dayVenueSession) {
    return programs;
  }
  if (
    programIdentityKey(dayVenueSession) !== programIdentityKey(editedProgram)
  ) {
    return programs;
  }

  const previousDayVenue = programsOnDay(previousPrograms, dayKey)[0];
  if (!previousDayVenue) {
    return programs;
  }

  return programs.map((program) => {
    if (getDayKeyInTimezone(program.startTime, "UTC") !== dayKey) {
      return program;
    }
    if (programIdentityKey(program) === programIdentityKey(editedProgram)) {
      return program;
    }
    if (!mirrorsVenue(program, previousDayVenue)) {
      return program;
    }
    return clearEmptyVenue({
      ...program,
      venueName: editedProgram.venueName,
      location: editedProgram.location,
    });
  });
};

export const upsertProgramSession = (
  currentPrograms: IProgram[],
  session: IProgram,
  editingSession: IProgram | null,
): IProgram[] => {
  const sessionIndex = editingSession
    ? currentPrograms.findIndex(
        (program) =>
          programIdentityKey(program) === programIdentityKey(editingSession),
      )
    : -1;

  const updatedPrograms = [...currentPrograms];
  let savedProgram: IProgram;

  if (sessionIndex === -1) {
    savedProgram = clearEmptyVenue({ ...session });
    updatedPrograms.push(savedProgram);
  } else {
    savedProgram = clearEmptyVenue({
      ...currentPrograms[sessionIndex],
      ...session,
    });
    updatedPrograms[sessionIndex] = savedProgram;
  }

  return applyDayVenue(updatedPrograms, savedProgram, currentPrograms);
};
